/**
 * API Endpoints Helper
 * 
 * Centralized endpoint construction following RESTful conventions
 * Base URL: /api/v1
 */

const API_VERSION = '/api/v1';

// Auth endpoints
export const AUTH_ENDPOINTS = {
  LOGIN: `${API_VERSION}/auth/login`,
  REFRESH: `${API_VERSION}/auth/refresh`,
  CHANGE_PASSWORD: `${API_VERSION}/auth/password`,
  PROFILE: `${API_VERSION}/auth/profile`,
  UPDATE_PROFILE: `${API_VERSION}/auth/profile`,
} as const;

// User management endpoints
export const USER_ENDPOINTS = {
  LIST: `${API_VERSION}/usuarios`,
  CREATE: `${API_VERSION}/usuarios`,
  GET: (id: string) => `${API_VERSION}/usuarios/${id}`,
  UPDATE: (id: string) => `${API_VERSION}/usuarios/${id}`,
  DELETE: (id: string) => `${API_VERSION}/usuarios/${id}`,
  ADMIN_UPDATE_PASSWORD: (id: string) => `${API_VERSION}/usuarios/${id}/password`,
} as const;

// Dashboard endpoints
export const DASHBOARD_ENDPOINTS = {
  STATS: `${API_VERSION}/dashboard/stats`,
  LOTIFICACIONES: `${API_VERSION}/dashboard/lotificaciones`,
  LOTIFICACION_KPI: (id: string) => `${API_VERSION}/dashboard/lotificaciones/${id}`,
  // Extended indicators
  LOTIFICACIONES_INDICADORES: `${API_VERSION}/dashboard/lotificaciones/indicadores`,
  LOTIFICACION_INDICADORES: (id: string) => 
    `${API_VERSION}/dashboard/lotificaciones/${id}/indicadores`,
} as const;

// Lotificaciones endpoints
export const LOTIFICACION_ENDPOINTS = {
  LIST: `${API_VERSION}/lotificaciones`,
  CREATE: `${API_VERSION}/lotificaciones`,
  GET: (id: string) => `${API_VERSION}/lotificaciones/${id}`,
  UPDATE: (id: string) => `${API_VERSION}/lotificaciones/${id}`,
  DELETE: (id: string) => `${API_VERSION}/lotificaciones/${id}`,
} as const;

// Poligonos endpoints
export const POLIGONO_ENDPOINTS = {
  LIST: `${API_VERSION}/poligonos`,
  CREATE: `${API_VERSION}/poligonos`,
  GET: (id: string) => `${API_VERSION}/poligonos/${id}`,
  UPDATE: (id: string) => `${API_VERSION}/poligonos/${id}`,
  DELETE: (id: string) => `${API_VERSION}/poligonos/${id}`,
  BY_LOTIFICACION: (lotificacionId: string) => 
    `${API_VERSION}/lotificaciones/${lotificacionId}/poligonos`,
  CREATE_IN_LOTIFICACION: (lotificacionId: string) =>
    `${API_VERSION}/lotificaciones/${lotificacionId}/poligonos`,
} as const;

// Lotes endpoints
export const LOTE_ENDPOINTS = {
  LIST: `${API_VERSION}/lotes`,
  CREATE: `${API_VERSION}/lotes`,
  GET: (id: string) => `${API_VERSION}/lotes/${id}`,
  UPDATE: (id: string) => `${API_VERSION}/lotes/${id}`,
  DELETE: (id: string) => `${API_VERSION}/lotes/${id}`,
  // Reservas
  CREATE_RESERVA: (id: string) => `${API_VERSION}/lotes/${id}/reservas`,
  ADD_COMPROBANTES: (id: string, resId: string) => `${API_VERSION}/lotes/${id}/reservas/${resId}/comprobantes`,
  DELETE_COMPROBANTE: (id: string, resId: string, compId: string) => `${API_VERSION}/lotes/${id}/reservas/${resId}/comprobantes/${compId}`,
  CANCEL_RESERVA: (id: string, resId: string) => `${API_VERSION}/lotes/${id}/reservas/${resId}/cancel`,
  // Pago Prima
  ADD_PAGO_PRIMA_COMPROBANTES: (id: string) => `${API_VERSION}/lotes/${id}/pago-prima/comprobantes`,
  DELETE_PAGO_PRIMA_COMPROBANTE: (id: string, compId: string) => `${API_VERSION}/lotes/${id}/pago-prima/comprobantes/${compId}`,
  // Ventas
  VENDER: (id: string) => `${API_VERSION}/lotes/${id}/vender`,
  // Pagos de Mensualidades
  REGISTRO_PAGO_MENSUALIDAD: (id: string) => `${API_VERSION}/lotes/${id}/pagos-mensualidades`,
  HISTORIAL_PAGOS_MENSUALIDADES: (id: string) => `${API_VERSION}/lotes/${id}/pagos-mensualidades`,
  RESUMEN_PAGOS_MENSUALIDADES: (id: string) => `${API_VERSION}/lotes/${id}/pagos-mensualidades/resumen`,
  CUOTAS_PENDIENTES: (id: string) => `${API_VERSION}/lotes/${id}/pagos-mensualidades/cuotas-pendientes`,
  // Admin actions
  REVERTIR: (id: string) => `${API_VERSION}/lotes/${id}/revertir`,
  // Legacy/Other
  BY_POLIGONO: (poligonoId: string) => `${API_VERSION}/poligonos/${poligonoId}/lotes`,
  IMPORT_TO_POLIGONO: (poligonoId: string) => `${API_VERSION}/poligonos/${poligonoId}/lotes/import`,
  // Templates
  TEMPLATE: `${API_VERSION}/poligonos/lotes/template`,
} as const;

// Compradores endpoints
export const COMPRADOR_ENDPOINTS = {
  LIST: `${API_VERSION}/compradores`,
  CREATE: `${API_VERSION}/compradores`,
  GET: (id: string) => `${API_VERSION}/compradores/${id}`,
  UPDATE: (id: string) => `${API_VERSION}/compradores/${id}`,
  DELETE: (id: string) => `${API_VERSION}/compradores/${id}`,
  SEARCH: `${API_VERSION}/compradores/search`,
} as const;

// Reservas endpoints
export const RESERVA_ENDPOINTS = {
  VENCIDAS: `${API_VERSION}/reservas/vencidas`,
  POR_VENCER: (dias: number) => `${API_VERSION}/reservas/por-vencer/${dias}`,
  EXTEND: (id: string) => `${API_VERSION}/reservas/${id}/extender`,
} as const;

// Reportes endpoints
export const REPORTE_ENDPOINTS = {
  RESERVAS: `${API_VERSION}/reportes/reservas`,
  VENTAS: `${API_VERSION}/reportes/ventas`,
  COMISIONES: `${API_VERSION}/reportes/comisiones`,
} as const;

// File upload endpoints
export const UPLOAD_ENDPOINTS = {
  UPLOADS: `${API_VERSION}/uploads`,
  /**
   * Returns the full API path to retrieve a file associated to a lote
   * The input should be a relative storage path like: "lotes/<loteId>/<tipo>/<filename>"
   */
  GET_FILE: (relativePath: string) => `${API_VERSION}/uploads/${relativePath}`,
  /**
   * Returns the full API path to delete a file associated to a lote (ADMIN only)
   * The input should be a relative storage path like: "lotes/<loteId>/<tipo>/<filename>"
   */
  DELETE_FILE: (relativePath: string) => `${API_VERSION}/uploads/${relativePath}`,
} as const;

// Helper function to build query string from params
export const buildQueryString = (params: Record<string, any>): string => {
  const filtered = Object.entries(params).filter(([, value]) => 
    value !== undefined && value !== null && value !== ''
  );
  
  if (filtered.length === 0) return '';
  
  const searchParams = new URLSearchParams();
  filtered.forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });
  
  return `?${searchParams.toString()}`;
};

// Helper function to build paginated list URL
export const buildPaginatedUrl = (
  baseUrl: string, 
  params: {
    page?: number;
    limit?: number;
    [key: string]: any;
  } = {}
): string => {
  return `${baseUrl}${buildQueryString(params)}`;
};